import { ChordLib } from '../chord/index.js'
import type { Chord, ChordVariant } from '../chord/chord-types.js'
import { isChordVariant } from '../chord/chord-types.js'
import { getNoteChromaticDistance } from '../note/note-lib.js'

/**
 * Music-theoretic description of the motion between two consecutive chords.
 * Returned `lexiconId` matches a `transition-*` entry in
 * `lib/music-lib/src/lexicon/lexicon-entries.ts`; the rendering surface
 * resolves it via `useTheoryCopy` so the wording follows the user's
 * `theoryLevel`.
 *
 * The helper is intentionally small — it covers the motion families the
 * Phase-1 explainer surfaces (descending/ascending fifth, stepwise,
 * common-tone pivot, chromatic neighbour). Anything else returns `null`
 * and the explainer hides the line (no invented narrative — Bowie's
 * "honest empty space" invariant).
 */
export type TransitionLexiconId =
  | 'transition-descending-fifth'
  | 'transition-ascending-fifth'
  | 'transition-stepwise-up'
  | 'transition-stepwise-down'
  | 'transition-common-tone'
  | 'transition-chromatic-neighbour'

export interface TransitionDescription {
  lexiconId: TransitionLexiconId
}

export interface DescribeTransitionInput {
  previous: Chord | null
  current: Chord
}

/** Number of common pitch classes between two chords. ChordNotes pass through `getNotes` unchanged. */
const commonToneCount = (a: Chord, b: Chord): number => {
  const aNotes = new Set(ChordLib.getNotes(a))
  let shared = 0
  for (const n of ChordLib.getNotes(b)) {
    if (aNotes.has(n)) {
      shared++
    }
  }
  return shared
}

const rootOf = (chord: Chord): ChordVariant['root'] | null =>
  isChordVariant(chord) ? chord.root : null

/**
 * Classify the root-motion interval between two chords as a music-theoretic
 * transition. Ordering matters:
 *
 *   1. Descending fifth (V → I) — strongest functional motion, beats out any
 *      common-tone match.
 *   2. Ascending fifth.
 *   3. Chromatic neighbour — half-step root motion with NO common tone
 *      (typical of parallel power-chord moves like C5 → C#5). This is
 *      labelled distinctly from stepwise because there's nothing harmonic
 *      to grab onto; the listener hears the slide.
 *   4. Stepwise (half-step or whole-step) up / down — diatonic-feeling
 *      stepwise motion (some common tones likely; if none we'd already
 *      have caught chromatic-neighbour).
 *   5. Common-tone pivot — any other motion where the two voicings share
 *      at least one pitch (e.g., C → Am sharing C and E).
 *   6. Otherwise null (rare leap, no common tone) — the explainer hides
 *      the line rather than inventing copy.
 *
 * Returns `null` when:
 *   - there is no previous chord,
 *   - either chord lacks a root (ChordNotes without a triad variant),
 *   - the roots are equal (no movement worth labeling),
 *   - the motion is a non-stepwise, non-fifth leap with no common tone.
 */
export const describeTransition = (input: DescribeTransitionInput): TransitionDescription | null => {
  const { previous, current } = input
  if (!previous) {
    return null
  }
  const prevRoot = rootOf(previous)
  const currRoot = rootOf(current)
  if (!prevRoot || !currRoot || prevRoot === currRoot) {
    return null
  }

  // Chromatic distance in [0, 12) measured ascending from prevRoot.
  const upDistance = getNoteChromaticDistance(prevRoot, currRoot)

  if (upDistance === 5) {
    return { lexiconId: 'transition-descending-fifth' }
  }
  if (upDistance === 7) {
    return { lexiconId: 'transition-ascending-fifth' }
  }

  const sharedTones = commonToneCount(previous, current)

  // Chromatic-neighbour is the parallel-slide case where the motion is
  // genuinely non-functional and the listener hears a slide rather than
  // a chord change. Phase-1.5 broadens this from the original power-chord-
  // only rule to: BOTH chords share the same `variant` (parallel motion —
  // major→major, minor→minor, dim→dim, power→power, m7→m7, etc.), motion
  // is a half-step, and they share no common tones.
  //
  // Bowie's music-theoretic rationale: when chord shapes are identical and
  // root motion is a half-step with zero pitch-class overlap (B→C major,
  // Em→Fm, C5→C#5, Cm7→C#m7), the ear parses parallel slide — the second
  // chord is the first shape transposed up a half-step. When the variants
  // differ (E→F where E is major and F is major BUT they share no tones —
  // hmm same variant; better example: B→Cm — different variants), the
  // listener parses two distinct harmonic identities and we fall through
  // to stepwise. When tones DO overlap (Cmaj7→C#maj7 share C), the move
  // is felt as voice-leading not slide and also falls through to stepwise.
  const sameVariant =
    isChordVariant(previous) && isChordVariant(current) && previous.variant === current.variant
  if (sameVariant && (upDistance === 1 || upDistance === 11) && sharedTones === 0) {
    return { lexiconId: 'transition-chromatic-neighbour' }
  }

  if (upDistance === 1 || upDistance === 2) {
    return { lexiconId: 'transition-stepwise-up' }
  }
  if (upDistance === 10 || upDistance === 11) {
    return { lexiconId: 'transition-stepwise-down' }
  }

  if (sharedTones > 0) {
    return { lexiconId: 'transition-common-tone' }
  }

  return null
}
